// Shape-permissive normalizer that converts any of three alert payload
// shapes into the FE-canonical AlertEvent:
//
//   1. WS OutgoingAlert        (event_bridge.broadcast_alert) —
//                              nested {camera:{id,name}, zone:{label},
//                              detection:{bbox,person_count}, actions:{}}
//   2. /events/by-id detail    — flat EventDetail with `event_id`
//   3. /events/alerts-list row — schema-v3 ai_events doc with
//                              `event_id`, `window_opened_at`, `ai_type`,
//                              `class_counts: {cls: {max_in_zone,...}}`
//
// Returning null lets the call site silently drop unrecognised payloads
// (defensive: a single malformed event must not poison the store).
import type { AlertEvent, EventSeverity } from '@/types/event';

// Backend uses {low, medium, high, critical}. FE displays {info, warning,
// critical}. Map both so we accept past + future payloads.
const SEVERITY_MAP: Record<string, EventSeverity> = {
  info: 'info',
  low: 'info',
  warning: 'warning',
  medium: 'warning',
  high: 'warning',
  critical: 'critical',
};

export const normalizeAlert = (x: unknown): AlertEvent | null => {
  if (!x || typeof x !== 'object') return null;
  const o = x as Record<string, unknown>;

  // ID source priority:
  //   1. top-level `id`        — WS OutgoingAlert + legacy HTTP
  //   2. `event_id`            — schema-v3 alert-window doc + /by-id
  // Without the `event_id` fallback the dashboard seed silently dropped
  // every row because /alerts-list never has `id`.
  const id =
    typeof o.id === 'string' ? o.id
      : typeof o.event_id === 'string' ? o.event_id
      : null;
  if (!id) return null;

  // Camera id can live flat (legacy + HTTP) or nested under `camera`.
  let cameraId = '';
  let cameraName: string | undefined;
  if (typeof o.camera_id === 'string') cameraId = o.camera_id;
  else if (o.camera && typeof o.camera === 'object') {
    const cam = o.camera as Record<string, unknown>;
    if (typeof cam.id === 'string') cameraId = cam.id;
    if (typeof cam.name === 'string') cameraName = cam.name;
  }
  if (!cameraId) return null;
  if (!cameraName && typeof o.camera_name === 'string') {
    cameraName = o.camera_name;
  }

  // Severity mapping with a safe fallback. Unknown values surface as
  // `info` rather than throwing — a bad severity string must not block
  // the alert from being recorded.
  const rawSev = typeof o.severity === 'string' ? o.severity.toLowerCase() : '';
  const severity: EventSeverity = SEVERITY_MAP[rawSev] ?? 'info';

  // Zone label sourced from WS `zone.label`, HTTP top-level fields, or
  // a few legacy aliases the older AI worker still emits.
  let zoneLabel = '';
  if (o.zone && typeof o.zone === 'object') {
    const z = o.zone as Record<string, unknown>;
    if (typeof z.label === 'string') zoneLabel = z.label;
  }
  if (!zoneLabel && typeof o.zone_label === 'string') zoneLabel = o.zone_label;
  if (!zoneLabel && typeof o.polygon_label === 'string') zoneLabel = o.polygon_label;
  if (!zoneLabel && typeof o.zone_name === 'string') zoneLabel = o.zone_name;

  // Display label: prefer the explicit flat `label`, else zone, else type.
  let label = typeof o.label === 'string' ? o.label : '';
  if (!label && zoneLabel) label = zoneLabel;
  if (!label && typeof o.type === 'string') label = o.type;

  // Timestamp: WS uses `ts`, HTTP detail uses `timestamp`, alert-window
  // docs additionally carry `window_opened_at` (preferred over `timestamp`
  // for historical rows because `timestamp` is the source-clock capture
  // time for the FIRST detection in the window — `window_opened_at` is
  // the open instant on the backend clock, which is what the chart
  // buckets).
  const timestamp =
    typeof o.window_opened_at === 'string' ? o.window_opened_at
      : typeof o.timestamp === 'string' ? o.timestamp
      : typeof o.ts === 'string' ? o.ts
      : new Date().toISOString();

  // Model: prefer `ai_type` (the user-facing category like "ppe" /
  // "intrusion" that the timeline chart buckets by) when present —
  // schema-v3 alert-window docs store the ONNX file name in `model`
  // (e.g. "yolov8s_ppe"), which doesn't match the chart's "ppe" /
  // "intrusion" legend. Falls back to flat `model` (WS legacy) then to
  // the type derivation for old payloads.
  let model = typeof o.ai_type === 'string' ? o.ai_type : '';
  if (!model && typeof o.model === 'string') model = o.model;
  if (!model && typeof o.type === 'string') {
    model = o.type.replace(/_alert$/, '').replace(/_cleared$/, '');
  }

  // Notification flags. WS provides `actions.audio` / `actions.telegram`;
  // HTTP doesn't ship them so we default to undefined and the FE treats
  // undefined as "allow" (matches the historical always-chime behaviour
  // for legacy alerts).
  let audioOn: boolean | undefined;
  let telegramOn: boolean | undefined;
  if (o.actions && typeof o.actions === 'object') {
    const a = o.actions as Record<string, unknown>;
    if (typeof a.audio === 'boolean') audioOn = a.audio;
    else if (typeof a.sound === 'boolean') audioOn = a.sound; // legacy key
    if (typeof a.telegram === 'boolean') telegramOn = a.telegram;
  }
  if (audioOn === undefined && typeof o.audio_alert_enabled === 'boolean') {
    audioOn = o.audio_alert_enabled;
  }
  if (telegramOn === undefined && typeof o.telegram_enabled === 'boolean') {
    telegramOn = o.telegram_enabled;
  }

  // bboxes: WS sends a single `detection.bbox` (4-tuple); HTTP can send
  // an array. Normalize both into an array shape so the renderer doesn't
  // branch on input source.
  let bboxes: AlertEvent['bboxes'];
  let personCount: number | undefined;
  if (Array.isArray(o.bboxes)) {
    bboxes = o.bboxes as AlertEvent['bboxes'];
  } else if (o.detection && typeof o.detection === 'object') {
    const d = o.detection as Record<string, unknown>;
    if (Array.isArray(d.bbox) && d.bbox.length >= 4) {
      const [x, y, x2, y2] = d.bbox as number[];
      bboxes = [{ x, y, w: Math.max(0, x2 - x), h: Math.max(0, y2 - y) }];
    }
    if (typeof d.person_count === 'number') personCount = d.person_count;
  }
  if (personCount === undefined && typeof o.person_count === 'number') {
    personCount = o.person_count;
  }

  // Per-class counts. Two shapes the backend may emit:
  //   1. WS payload alongside detection: `{class_counts: {no_helmet: 2}}`
  //   2. HTTP alert window doc: `class_counts: {no_helmet: {max_in_zone, max_total, max_conf}}`
  // Flatten both to `{class: count}` (using max_in_zone when the rich
  // object form is present — that's the "peak inside zones" headline).
  let classCounts: Record<string, number> | undefined;
  if (o.class_counts && typeof o.class_counts === 'object') {
    const raw = o.class_counts as Record<string, unknown>;
    const flat: Record<string, number> = {};
    for (const [cls, val] of Object.entries(raw)) {
      if (typeof val === 'number') {
        if (val > 0) flat[cls] = val;
      } else if (val && typeof val === 'object') {
        const v = val as Record<string, unknown>;
        const n = typeof v.max_in_zone === 'number'
          ? v.max_in_zone
          : typeof v.max_total === 'number'
            ? v.max_total
            : 0;
        if (n > 0) flat[cls] = n;
      }
    }
    if (Object.keys(flat).length > 0) classCounts = flat;
  }

  return {
    id,
    camera_id: cameraId,
    camera_name: cameraName,
    model,
    severity,
    label,
    zone_label: zoneLabel || undefined,
    person_count: personCount,
    class_counts: classCounts,
    timestamp,
    bboxes,
    audio_alert_enabled: audioOn,
    telegram_enabled: telegramOn,
  };
};
